import { useState } from "react"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Share2, Copy, MessageCircle, Mail} from "lucide-react"
import { useAnalytics } from "@/hooks/useAnalytics"

interface ShareModalProps {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  title: string
  description: string
  url: string
  imageUrl?: string
  itemId: string
  itemType: 'flash' | 'tattoo' | 'healed'
  artistId: string
  source?: string
}

const isMobileDevice = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
         (navigator.maxTouchPoints && navigator.maxTouchPoints > 2)
}

export function ShareModal({ 
  isOpen, 
  onOpenChange, 
  title, 
  description, 
  url, 
  imageUrl,
  itemId,
  itemType,
  artistId,
  source
}: ShareModalProps) {
  const [copied, setCopied] = useState(false)
  const { trackEvent } = useAnalytics()

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(url)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
      
      await trackEvent('item_shared', {
        item_id: itemId,
        item_type: itemType,
        artist_id: artistId,
        source,
        share_method: 'clipboard'
      })
    } catch (error) {
      console.error('Failed to copy link:', error)
    }
  }

  const handleWhatsAppShare = async () => {
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(`${title}\n${description}\n${url}`)}`
    window.open(whatsappUrl, '_blank')
    
    await trackEvent('item_shared', {
      item_id: itemId,
      item_type: itemType,
      artist_id: artistId,
      source,
      share_method: 'whatsapp'
    })
  }

  const handleEmailShare = async () => {
    const emailUrl = `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(`${description}\n\n${url}`)}`
    window.open(emailUrl, '_blank')
    
    await trackEvent('item_shared', {
      item_id: itemId,
      item_type: itemType,
      artist_id: artistId,
      source,
      share_method: 'email'
    })
  }

  const handleShare = async () => {
    const isMobile = isMobileDevice()
    
    if (isMobile && typeof navigator.share === 'function') {
      // Mobile device with native share support - use native share
      try {
        await navigator.share({
          title,
          text: description,
          url
        })
        
        await trackEvent('item_shared', {
          item_id: itemId,
          item_type: itemType,
          artist_id: artistId,
          source,
          share_method: 'native'
        })
        
        onOpenChange(false) // Close modal after sharing
      } catch (error) {
        console.error('Native share failed:', error)
        // Fallback to copy link
        await handleCopyLink()
      }
    } else {
      // Desktop or no native share support - copy link and show feedback
      await handleCopyLink()
      // Auto-close modal after a short delay on desktop
      setTimeout(() => {
        onOpenChange(false)
      }, 1500)
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Share2 className="w-5 h-5" />
            Share Flash
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Preview */}
          <div className="flex items-center gap-3 p-3 bg-muted rounded-lg">
            {imageUrl && (
              <img 
                src={imageUrl} 
                alt="Flash preview" 
                className="w-12 h-12 object-cover rounded"
              />
            )}
            <div className="flex-1 min-w-0">
              <p className="font-medium truncate">{title}</p>
              <p className="text-sm text-muted-foreground truncate">{description}</p>
            </div>
          </div>

          {/* Share Options */}
          <div className="grid grid-cols-2 gap-3">
            <Button
              variant="outline"
              onClick={handleShare}
              className="flex items-center gap-2"
            >
              <Share2 className="w-4 h-4" />
              {isMobileDevice() ? 'Share' : (copied ? 'Copied!' : 'Copy Link')}
            </Button>
            
            <Button
              variant="outline"
              onClick={handleCopyLink}
              className="flex items-center gap-2"
            >
              <Copy className="w-4 h-4" />
              {copied ? 'Copied!' : 'Copy Link'}
            </Button>
            
            <Button
              variant="outline"
              onClick={handleWhatsAppShare}
              className="flex items-center gap-2"
            >
              <MessageCircle className="w-4 h-4" />
              WhatsApp
            </Button>
            
            <Button
              variant="outline"
              onClick={handleEmailShare}
              className="flex items-center gap-2"
            >
              <Mail className="w-4 h-4" />
              Email
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
} 
